/**
 * Auth Resources
 *
 * Provides access to @classic-homes/auth package documentation.
 */

import { McpServer } from '@modelcontextprotocol/sdk/server/mcp.js';
import { ResourceTemplate } from '@modelcontextprotocol/sdk/server/mcp.js';
import authCatalog from '../data/auth-catalog.json' with { type: 'json' };
import type { AuthCatalog } from '../types.js';

const catalog = authCatalog as unknown as AuthCatalog;

/**
 * Register auth-related resources
 */
export function registerAuthResources(server: McpServer): void {
  // Full auth catalog
  server.resource('Auth Package', 'auth://all', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(catalog, null, 2),
      },
    ],
  }));

  // Core subpackage
  server.resource('Auth Core', 'auth://core', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.core.importPath,
            description: catalog.subpackages.core.description,
            modules: catalog.subpackages.core.modules,
            types: catalog.subpackages.core.types,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Svelte subpackage
  server.resource('Auth Svelte', 'auth://svelte', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.svelte.importPath,
            description: catalog.subpackages.svelte.description,
            modules: catalog.subpackages.svelte.modules,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Testing subpackage
  server.resource('Auth Testing', 'auth://testing', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            importPath: catalog.subpackages.testing.importPath,
            description: catalog.subpackages.testing.description,
            modules: catalog.subpackages.testing.modules,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Types only
  server.resource('Auth Types', 'auth://types', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description: 'TypeScript types exported from @classic-homes/auth/core',
            types: catalog.subpackages.core.types,
          },
          null,
          2
        ),
      },
    ],
  }));

  // API operations only
  server.resource('Auth API', 'auth://api', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description: 'Auth API client methods from @classic-homes/auth/core',
            api: catalog.subpackages.core.modules.api,
          },
          null,
          2
        ),
      },
    ],
  }));

  // Individual core module access via template
  server.resource(
    'Auth Core Module',
    new ResourceTemplate('auth://core/{module}', { list: undefined }),
    async (uri, params) => {
      const moduleName = params.module as string;
      const modules = catalog.subpackages.core.modules;
      const module = modules[moduleName as keyof typeof modules];

      if (!module) {
        const availableModules = Object.keys(modules);
        return {
          contents: [
            {
              uri: uri.href,
              mimeType: 'application/json',
              text: JSON.stringify({
                error: `Module "${moduleName}" not found in core`,
                availableModules,
              }),
            },
          ],
        };
      }

      return {
        contents: [
          {
            uri: uri.href,
            mimeType: 'application/json',
            text: JSON.stringify(module, null, 2),
          },
        ],
      };
    }
  );

  // Individual Svelte module access via template
  server.resource(
    'Auth Svelte Module',
    new ResourceTemplate('auth://svelte/{module}', { list: undefined }),
    async (uri, params) => {
      const moduleName = params.module as string;
      const modules = catalog.subpackages.svelte.modules;
      const module = modules[moduleName as keyof typeof modules];

      if (!module) {
        const availableModules = Object.keys(modules);
        return {
          contents: [
            {
              uri: uri.href,
              mimeType: 'application/json',
              text: JSON.stringify({
                error: `Module "${moduleName}" not found in svelte`,
                availableModules,
              }),
            },
          ],
        };
      }

      return {
        contents: [
          {
            uri: uri.href,
            mimeType: 'application/json',
            text: JSON.stringify(module, null, 2),
          },
        ],
      };
    }
  );

  // Quick reference for common operations
  server.resource('Auth Quick Reference', 'auth://quick-reference', async (uri) => ({
    contents: [
      {
        uri: uri.href,
        mimeType: 'application/json',
        text: JSON.stringify(
          {
            description: 'Quick reference for common auth operations',
            initialization: {
              code: "import { initAuth } from '@classic-homes/auth/core';\n\ninitAuth({ baseUrl: 'https://api.example.com' });",
              description: 'Initialize the auth system with your API base URL',
            },
            createAuthClient: {
              code: "import { createAuthClient } from '@classic-homes/auth/svelte';\n\nexport const { authStore, authActions, authService, isAuthenticated, currentUser } = createAuthClient({\n  baseUrl: 'https://api.example.com',\n  onSessionExpired: (path) => goto(`/login?redirect=${path}`),\n});",
              description: 'One-liner setup that bundles initialization, stores, and services',
            },
            login: {
              code: "import { authApi, isMfaChallengeResponse } from '@classic-homes/auth/core';\n\nconst response = await authApi.login({ username: 'user@example.com', password: 'password' });\nif (isMfaChallengeResponse(response)) {\n  // Handle MFA\n}",
              description: 'Login with username and password, handling MFA if required',
            },
            svelteStore: {
              code: "import { isAuthenticated, currentUser, authActions } from '@classic-homes/auth/svelte';\n\n// In component\n{#if $isAuthenticated}\n  <p>Welcome, {$currentUser.firstName}!</p>\n{/if}",
              description: 'Use reactive Svelte stores for auth state',
            },
            routeProtection: {
              code: "import { requireAuth, requireRole } from '@classic-homes/auth/svelte';\nimport { redirect } from '@sveltejs/kit';\n\n// In +page.ts\nexport function load() {\n  const result = requireAuth();\n  if (!result.allowed) throw redirect(302, result.redirectTo ?? '/login');\n}",
              description: 'Protect SvelteKit routes with auth guards',
            },
            serverSideHooks: {
              code: "// hooks.server.ts\nimport { createAuthHook, routePatterns } from '@classic-homes/auth/svelte';\nimport { sequence } from '@sveltejs/kit/hooks';\n\nconst authHook = createAuthHook({\n  publicRoutes: [routePatterns.auth, routePatterns.health],\n});\n\nexport const handle = sequence(authHook);",
              description: 'Server-side authentication middleware for SvelteKit',
            },
            navigationFiltering: {
              code: "import { filterByAccess } from '@classic-homes/auth/svelte';\n\nconst navItems = [\n  { id: 'dashboard', label: 'Dashboard' },\n  { id: 'admin', label: 'Admin', roles: ['admin'] },\n];\n\nconst filtered = filterByAccess(navItems, user);",
              description: 'Filter navigation items based on user roles/permissions',
            },
            userDisplay: {
              code: "import { getDisplayName, getUserInitials, getGreeting } from '@classic-homes/auth/core';\n\ngetDisplayName(user);     // 'John Doe'\ngetUserInitials(user);    // 'JD'\ngetGreeting(user);        // 'Good morning, John'",
              description: 'User display utilities for UI',
            },
            roleValidation: {
              code: "import { authService, RoleDeniedError } from '@classic-homes/auth/core';\n\ntry {\n  await authService.login(credentials, { allowedRoles: ['admin', 'agent'] });\n} catch (e) {\n  if (e instanceof RoleDeniedError) {\n    // User authenticated but lacks role\n  }\n}",
              description: 'Validate roles during login and handle access denial',
            },
            testing: {
              code: "import { setupTestAuth, mockUser, assertAuthenticated } from '@classic-homes/auth/testing';\n\nconst ctx = setupTestAuth();\n// Run tests\nctx.cleanup();",
              description: 'Set up mocks for testing auth flows',
            },
          },
          null,
          2
        ),
      },
    ],
  }));
}
